<script setup>
import { computed } from 'vue'
import { usePlayerService } from '@composables/use-player-service'

const BAR_COUNT = 140
const MIN_BAR_HEIGHT = 0.1

const props = defineProps({
    seed: {
        type: [Number, String],
        default: 0,
    },
    timeFraction: {
        type: Number,
        default: 0,
    },
    duration: {
        type: Number,
        default: 100,
    },
})

const playerService = usePlayerService()

function buildBars(seed) {
    const text = String(seed)
    let state = 2166136261
    for (let i = 0; i < text.length; i++) {
        state = Math.imul(state ^ text.charCodeAt(i), 16777619)
    }

    return Array.from({ length: BAR_COUNT }, () => {
        state = (Math.imul(state, 1664525) + 1013904223) >>> 0
        return Math.min(1, state / 4294967296 + MIN_BAR_HEIGHT)
    })
}

const bars = computed(() => buildBars(props.seed))

const invertedFraction = computed(() => 1 - props.timeFraction)

function seekTime({ offsetX, srcElement }) {
    playerService.seekTime((offsetX / srcElement.offsetWidth) * props.duration)
}
</script>

<template>
    <div class="waveform">
        <div
            class="waveform-track"
            @click.stop.prevent="seekTime"
        >
            <div class="waveform-layer">
                <div
                    v-for="(bar, index) in bars"
                    :key="index"
                    class="bar bar-bg"
                    :style="{ transform: `scaleY(${bar})` }"
                />
            </div>

            <div
                class="waveform-layer"
                :style="{ transform: `translateX(${invertedFraction * -100}%)` }"
            >
                <div
                    class="waveform-layer waveform-layer-active"
                    :style="{ transform: `translateX(${invertedFraction * 100}%)` }"
                >
                    <div
                        v-for="(bar, index) in bars"
                        :key="index"
                        class="bar"
                        :style="{ transform: `scaleY(${bar})` }"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

.waveform {
    position: relative;
    width: 100%;
    height: 100%;

    &-track {
        @include fill;

        display: flex;
        justify-content: space-between;
        overflow: hidden;
        cursor: pointer;
        background-color: $c-surface-overlay;

        @include desktop {
            background: none;
        }
    }

    &-layer {
        @include fill;

        display: flex;
        justify-content: space-between;
        overflow: hidden;
        pointer-events: none;
        backface-visibility: hidden;

        &-active {
            background-color: $c-white;

            @include desktop {
                background: none;
            }
        }
    }
}

.bar {
    display: none;
    width: 2px;
    height: 100%;
    background: $c-highlight;

    @include desktop {
        display: block;
    }
}

.bar-bg {
    background-color: $c-surface-overlay;
}
</style>
